/**
 * IDT デザイナー MyPage「スキル評価」タブ本体 (デザイン評価: 個人 vs 全体)。
 *
 * IDT 看板下部の「デザイン評価 (全体)」カードを個人視点に移植する。基準を「当期 vs 前期」から
 * 「個人 vs 全体」に変え、貢献度 (3軸) radar を中央に大きく / PJ解像度 (5軸, teal) radar を左に
 * 小さく配置する。推移モードは軸スイッチで 1 軸ずつ個人 (色) vs 全体平均 (灰) を折線表示する。
 *
 * Page ノードの boundary アノテーションは本体の export 宣言（DesignerSkillEvaluationTab）直上に置く。
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  useIdtDesignEvaluation,
  useIdtDesignEvaluationByDesigner,
} from '../../../hooks/useIdtKpi.js';
import { EvalRadar } from '../idt-kpi/DesignEvaluationRadar.js';
import { DesignEvalSelect } from '../idt-kpi/DesignEvalSelect.js';
import { TrendViewSwitch } from '../idt-kpi/TrendViewSwitch.js';
import { EvalModeSwitch, RadarLegend, type EvalMode } from '../idt-kpi/EvalChartControls.js';
import { monthLabelOf } from '../idt-kpi/periods.js';
import type { TrendView } from '../idt-kpi/shared.js';
import {
  CATEGORY_COLORS,
  EVALUATION_AXES,
  EVALUATION_RADIUS_DOMAIN,
  EVALUATION_TREND_DOMAIN,
  RADAR_PREV_COLOR,
  RESOLUTION_AXES,
  RESOLUTION_RADAR_COLOR,
  RESOLUTION_RADIUS_DOMAIN,
  RESOLUTION_TREND_DOMAIN,
  fyLabelOf,
  fyMonths,
  fyStartYearOf,
  type EvalSeriesPoint,
} from '../idt-kpi/designEvaluation.js';
import { colorVarOf } from '../idt-kpi/shared.js';
import { LineComparisonView, type SwitchableAxis } from './DesignerEvalAxisTrend.js';
import { DesignerCommentDetail } from './DesignerCommentDetail.js';
import type {
  IdtDesignEvaluationPoint,
  IdtDesignEvaluationQuarterPoint,
} from '../../../lib/api.js';

interface DesignerSkillEvaluationTabProps {
  /** ログイン中 (or 対象) デザイナーの nickname。内部で lower-case 化する */
  nickName: string;
}

/** 解像度 5 軸のスイッチ項目 (推移モード左列)。 @graph-connects none */
const RESOLUTION_SWITCH_AXES: SwitchableAxis[] = RESOLUTION_AXES.map((a) => ({
  ...a,
  domain: RESOLUTION_TREND_DOMAIN,
}));

/** 貢献度 3 軸のスイッチ項目 (推移モード右列)。 @graph-connects none */
const EVALUATION_SWITCH_AXES: SwitchableAxis[] = EVALUATION_AXES.map((a) => ({
  ...a,
  domain: EVALUATION_TREND_DOMAIN,
}));

/** "YYYY-MM"/"YYYY/NQ" を point から EvalSeriesPoint へ正規化する。 @graph-connects none */
function toSeries(
  points: (IdtDesignEvaluationPoint | IdtDesignEvaluationQuarterPoint)[],
): EvalSeriesPoint[] {
  return points.map((p) => ({
    period: 'month' in p ? p.month : p.quarter,
    resolution: p.resolution,
    evaluation: p.evaluation,
    evaluationCounts: p.evaluationCounts,
  }));
}

/** 解像度 or 貢献度に 1 つでも非 null の値があるか (未評価の当月などを既定から除くため)。 @graph-connects none */
function hasEvalData(p: EvalSeriesPoint): boolean {
  return (
    Object.values(p.resolution).some((v) => v != null) ||
    Object.values(p.evaluation).some((v) => v != null)
  );
}

/** PJ解像度 5 軸の点数を radar 下に横 1 列で表示する (軸名=分類色 / 点数=teal, 控えめサイズ)。 @graph-connects none */
function ResolutionScoreStrip({ selected }: { selected: EvalSeriesPoint }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-end justify-center gap-x-3.5 gap-y-1">
      {RESOLUTION_AXES.map((a) => {
        const value = selected.resolution[a.key];
        const labelColor = a.category ? CATEGORY_COLORS[a.category] : 'var(--color-text-secondary)';
        return (
          <div key={a.key} className="flex flex-col items-center gap-0.5 leading-none">
            <span className="text-[10px]" style={{ color: labelColor }}>
              {t(`idtKpi.designEvaluation.shortAxes.${a.key}`)}
            </span>
            <span
              className="font-mono text-sm font-bold tabular-nums"
              style={{ color: RESOLUTION_RADAR_COLOR }}
            >
              {value == null ? '—' : value.toFixed(2)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * IDT デザイナー MyPage「スキル評価」タブ。デザイン評価 (個人 vs 全体) を radar / 推移で表示する。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイナー個人のデザイン評価 (解像度/貢献度) を全体と比較表示するスキル評価タブ
 * @graph-connects useIdtDesignEvaluation [calls] 全体の月次/四半期評価系列取得
 * @graph-connects useIdtDesignEvaluationByDesigner [calls] デザイナー個人別の月次/四半期評価系列取得
 */
export function DesignerSkillEvaluationTab({ nickName }: DesignerSkillEvaluationTabProps) {
  const { t } = useTranslation();
  const nick = nickName.toLowerCase();
  const overallQuery = useIdtDesignEvaluation();
  const byDesignerQuery = useIdtDesignEvaluationByDesigner();

  const [mode, setMode] = useState<EvalMode>('radar');
  const [granularity, setGranularity] = useState<TrendView>('month');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [selectedFy, setSelectedFy] = useState<number | null>(null);

  const me = byDesignerQuery.data?.designers.find((d) => d.nickname === nick) ?? null;

  const selfMonth = useMemo(() => toSeries(me?.points ?? []), [me]);
  const selfQuarter = useMemo(() => toSeries(me?.quarterPoints ?? []), [me]);
  const overallMonth = useMemo(
    () => toSeries(overallQuery.data?.points ?? []),
    [overallQuery.data],
  );
  const overallQuarter = useMemo(
    () => toSeries(overallQuery.data?.quarterPoints ?? []),
    [overallQuery.data],
  );

  const isQuarter = granularity === 'quarter';
  const selfActive = isQuarter ? selfQuarter : selfMonth;
  const overallActive = isQuarter ? overallQuarter : overallMonth;

  const selfByPeriod = useMemo(() => new Map(selfActive.map((p) => [p.period, p])), [selfActive]);
  const overallByPeriod = useMemo(
    () => new Map(overallActive.map((p) => [p.period, p])),
    [overallActive],
  );
  // 推移折線は軸キー → 値 の 1 枚のレコードを見る (解像度 5 軸と貢献度 3 軸でキーは重複しない)。
  // radar と同じ汎用コンポーネントを DLE 評価と共有するため、折線用にだけ両者を畳んだ索引を作る。
  const selfAxisValues = useMemo(
    () => new Map(selfActive.map((p) => [p.period, { ...p.resolution, ...p.evaluation }])),
    [selfActive],
  );
  const overallAxisValues = useMemo(
    () => new Map(overallActive.map((p) => [p.period, { ...p.resolution, ...p.evaluation }])),
    [overallActive],
  );

  // ラベル整形 (月 = "2026年6月" / Q = シート表記 "2026/4Q")。
  const fullLabelOf = useMemo(
    () => (period: string) => (isQuarter ? period : monthLabelOf(period)),
    [isQuarter],
  );
  const shortLabelOf = useMemo(
    () => (period: string) =>
      isQuarter
        ? period.slice(2)
        : `${period.slice(2, 4)}/${String(parseInt(period.slice(5, 7), 10))}`,
    [isQuarter],
  );

  // radar の選択期間: 未指定なら「データがある最新期間」を既定にする (未評価の当月を避ける)。
  const latestSelfPeriod = useMemo(() => {
    for (let i = selfActive.length - 1; i >= 0; i--) {
      const p = selfActive[i];
      if (p && hasEvalData(p)) return p.period;
    }
    return selfActive.length > 0 ? (selfActive[selfActive.length - 1]?.period ?? null) : null;
  }, [selfActive]);
  const selectedPeriod = (isQuarter ? selectedQuarter : selectedMonth) ?? latestSelfPeriod;
  const selectedSelf = selectedPeriod != null ? (selfByPeriod.get(selectedPeriod) ?? null) : null;
  const selectedOverall =
    selectedPeriod != null ? (overallByPeriod.get(selectedPeriod) ?? null) : null;

  // 貢献度 radar の参照線: 選択期間の 1 つ前の「データのある」個人評価 (前月比の伸びを示す)。
  const selectedSelfPrev = useMemo(() => {
    if (selectedPeriod == null) return null;
    const idx = selfActive.findIndex((p) => p.period === selectedPeriod);
    if (idx <= 0) return null;
    for (let i = idx - 1; i >= 0; i--) {
      const p = selfActive[i];
      if (p && hasEvalData(p)) return p;
    }
    return null;
  }, [selfActive, selectedPeriod]);

  // 推移の x 軸 period: 月は FY 12ヶ月、Q は全体の全 Q (完全な時間軸)。
  const latestMonth =
    selfMonth.length > 0
      ? (selfMonth[selfMonth.length - 1]?.period ?? null)
      : overallMonth.length > 0
        ? (overallMonth[overallMonth.length - 1]?.period ?? null)
        : null;
  const effectiveFy = selectedFy ?? (latestMonth != null ? fyStartYearOf(latestMonth) : null);
  const linePeriods = useMemo(
    () =>
      isQuarter
        ? overallQuarter.map((p) => p.period)
        : effectiveFy != null
          ? fyMonths(effectiveFy)
          : [],
    [isQuarter, overallQuarter, effectiveFy],
  );

  // ドロップダウン候補。
  const radarOptions = useMemo(
    () =>
      [...selfActive]
        .reverse()
        .map((p) => ({ value: p.period, label: isQuarter ? p.period : monthLabelOf(p.period) })),
    [selfActive, isQuarter],
  );
  const fyOptions = useMemo(() => {
    const set = new Set<number>();
    for (const p of selfMonth) set.add(fyStartYearOf(p.period));
    for (const p of overallMonth) set.add(fyStartYearOf(p.period));
    return [...set].sort((a, b) => b - a).map((y) => ({ value: String(y), label: fyLabelOf(y) }));
  }, [selfMonth, overallMonth]);

  // ヘッダ右のドロップダウン (radar: 期間, line+月: FY)。
  let periodSelect = null;
  if (mode === 'radar' && selectedPeriod != null) {
    periodSelect = (
      <DesignEvalSelect
        options={radarOptions}
        value={selectedPeriod}
        onChange={isQuarter ? setSelectedQuarter : setSelectedMonth}
        ariaLabel={t('idtKpi.designEvaluation.periodSelectLabel')}
      />
    );
  } else if (mode === 'line' && !isQuarter && effectiveFy != null) {
    periodSelect = (
      <DesignEvalSelect
        options={fyOptions}
        value={String(effectiveFy)}
        onChange={(v) => {
          setSelectedFy(parseInt(v, 10));
        }}
        ariaLabel={t('idtKpi.designEvaluation.fySelectLabel')}
      />
    );
  }

  const isLoading = overallQuery.isLoading || byDesignerQuery.isLoading;
  const hasAny = selfActive.length > 0 || overallActive.length > 0;

  return (
    <Card className="animate-content-reveal">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('idtKpi.designEvaluation.personal.title')}
          </h3>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            {t('idtKpi.designEvaluation.personal.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {periodSelect}
          <TrendViewSwitch view={granularity} onChange={setGranularity} />
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">Loading…</div>
      ) : !hasAny ? (
        <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
          {t('idtKpi.designEvaluation.empty')}
        </div>
      ) : mode === 'radar' ? (
        <RadarView
          selectedSelf={selectedSelf}
          selectedOverall={selectedOverall}
          selectedSelfPrev={selectedSelfPrev}
          selectedLabel={selectedPeriod != null ? fullLabelOf(selectedPeriod) : ''}
          nickName={nickName}
          commentMonth={isQuarter ? null : selectedPeriod}
        />
      ) : (
        <LineComparisonView
          resolutionTitle={t('idtKpi.designEvaluation.resolutionTitle')}
          evaluationTitle={t('idtKpi.designEvaluation.evaluationTitle')}
          resolutionAxes={RESOLUTION_SWITCH_AXES}
          evaluationAxes={EVALUATION_SWITCH_AXES}
          selfByPeriod={selfAxisValues}
          overallByPeriod={overallAxisValues}
          periods={linePeriods}
          shortLabelOf={shortLabelOf}
          fullLabelOf={fullLabelOf}
          selfLabel={t('idtKpi.designEvaluation.personal.self')}
          overallLabel={t('idtKpi.designEvaluation.personal.overall')}
        />
      )}

      <div className="flex justify-end mt-4">
        <EvalModeSwitch
          mode={mode}
          onChange={setMode}
          radarLabel={t('idtKpi.designEvaluation.modeRadar')}
          lineLabel={t('idtKpi.designEvaluation.modeLine')}
          ariaLabel={t('idtKpi.designEvaluation.modeSwitchLabel')}
        />
      </div>
    </Card>
  );
}

/** radar モード: 貢献度 (中央・大) + PJ解像度 (左・小)、個人 vs 全体 (+ 前期間個人)。 @graph-connects none */
function RadarView({
  selectedSelf,
  selectedOverall,
  selectedSelfPrev,
  selectedLabel,
  nickName,
  commentMonth,
}: {
  selectedSelf: EvalSeriesPoint | null;
  selectedOverall: EvalSeriesPoint | null;
  selectedSelfPrev: EvalSeriesPoint | null;
  selectedLabel: string;
  nickName: string;
  commentMonth: string | null;
}) {
  const { t } = useTranslation();
  const selfLabel = t('idtKpi.designEvaluation.personal.self');
  const selfPrevLabel = t('idtKpi.designEvaluation.personal.selfPrev');
  const overallLabel = t('idtKpi.designEvaluation.personal.overall');
  // 貢献度 radar の個人系列は karte と同じ designer 主題色で描く (前期間個人はその淡色)。
  const themeColor = `var(${colorVarOf(nickName.toLowerCase())})`;
  const themeColorLight = `color-mix(in srgb, ${themeColor} 50%, var(--color-bg-surface))`;
  if (!selectedSelf || !hasEvalData(selectedSelf)) {
    return (
      <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('idtKpi.designEvaluation.personal.noSelf')}
      </div>
    );
  }
  const evaluationLegend = [
    { color: themeColor, label: selfLabel },
    ...(selectedSelfPrev ? [{ color: themeColorLight, label: selfPrevLabel }] : []),
    { color: RADAR_PREV_COLOR, label: overallLabel },
  ];
  return (
    // @container でカード実幅を基準にレイアウトを切替える。viewport の lg だとサイドバー展開で
    // カード幅が縮んでも 3 列を維持し、PJ解像度 radar (左 1fr) の描画域が極端に小さくなる。
    // 十分広い時のみ 3 列、中間幅は radar 2 列 + コメント全幅、最狭は 1 列に積む。
    <div className="@container flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-4 @[820px]:grid-cols-2 @[1340px]:grid-cols-[1fr_1.7fr_1fr]">
        {/* 左: PJ解像度 (小, teal)。radar は縦中央、点数は radar と凡例の間、凡例は最下段
            (= 貢献度 radar の凡例と同じ水平線) */}
        <div className="order-2 flex flex-col gap-1 @[820px]:order-1">
          <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] text-center">
            {t('idtKpi.designEvaluation.resolutionTitle')}
          </h4>
          <div className="flex flex-1 flex-col items-center justify-center gap-2">
            <div className="w-full">
              <EvalRadar
                axes={RESOLUTION_AXES}
                selected={selectedSelf.resolution}
                prev={selectedOverall?.resolution ?? null}
                height={380}
                domain={RESOLUTION_RADIUS_DOMAIN}
                currentColor={RESOLUTION_RADAR_COLOR}
                selectedLabel={selfLabel}
                prevLabel={overallLabel}
              />
            </div>
            {/* radar と凡例の間に各軸の点数を横 1 列 (控えめサイズ) */}
            <ResolutionScoreStrip selected={selectedSelf} />
          </div>
          <RadarLegend
            items={[
              { color: RESOLUTION_RADAR_COLOR, label: selfLabel },
              { color: RADAR_PREV_COLOR, label: overallLabel },
            ]}
          />
        </div>
        {/* 中央: チーム貢献度評価 (大, 主題色 / 前期間個人 = 淡主題色 / 軸ラベル外に 全体差分) */}
        <div className="order-1 flex flex-col gap-1 @[820px]:order-2">
          <h4 className="text-sm font-semibold text-[var(--color-text-secondary)] text-center">
            {t('idtKpi.designEvaluation.evaluationTitle')}
          </h4>
          <EvalRadar
            axes={EVALUATION_AXES}
            selected={selectedSelf.evaluation}
            prev={selectedOverall?.evaluation ?? null}
            extra={selectedSelfPrev?.evaluation ?? null}
            extraColor={themeColorLight}
            extraLabel={selfPrevLabel}
            height={620}
            domain={EVALUATION_RADIUS_DOMAIN}
            currentColor={themeColor}
            selectedLabel={selfLabel}
            prevLabel={overallLabel}
            axisCounts={selectedSelf.evaluationCounts}
            axisLabelColor="var(--color-text-primary)"
            showDiff
            diffPositiveColor={themeColor}
          />
          <RadarLegend items={evaluationLegend} />
        </div>
        {/* 右: 評価コメント詳細 (Good 上 / More 下)。中央 radar の右の余白を活用 */}
        <div className="order-3 @[820px]:col-span-2 @[1340px]:col-span-1 @[1340px]:self-stretch @[1340px]:pt-6">
          <DesignerCommentDetail
            nickName={nickName}
            month={commentMonth}
            monthLabel={selectedLabel}
          />
        </div>
      </div>
    </div>
  );
}
